import {
  collection,
  doc,
  getDocs, 
  getDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  addDoc,
  updateDoc,
  increment,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../lib/firebase';

export interface AnalyticsData {
  id: string;
  userId: string;
  contentId?: string;
  type: 'view' | 'engagement' | 'revenue' | 'demographic' | 'traffic';
  timestamp: Date;
  data: Record<string, any>;
}

export interface ViewAnalytics {
  contentId: string;
  views: number;
  uniqueViews: number;
  watchTime: number;
  completionRate: number;
  viewsOverTime: Array<{
    date: string;
    views: number;
    uniqueViews: number;
  }>;
}

export interface RevenueAnalytics {
  contentId: string;
  totalRevenue: number;
  adRevenue: number;
  donationRevenue: number;
  subscriptionRevenue: number;
  revenueOverTime: Array<{
    date: string;
    revenue: number;
    adRevenue: number;
    donationRevenue: number;
  }>;
}

export interface DemographicAnalytics {
  contentId: string;
  ageGroups: {
    '13-17': number;
    '18-24': number;
    '25-34': number;
    '35-44': number;
    '45-54': number;
    '55-64': number;
    '65+': number;
  };
  genderDistribution: {
    male: number;
    female: number;
    other: number;
    preferNotToSay: number;
  };
  locationDistribution: Array<{
    country: string;
    views: number;
    percentage: number;
  }>;
}

export interface EngagementAnalytics {
  contentId: string;
  likes: number;
  dislikes: number;
  comments: number;
  shares: number;
  bookmarks: number;
  subscriptions: number;
  engagementRate: number;
  averageWatchTime: number;
  retentionRate: number;
}

export interface TrafficAnalytics {
  totalViews: number;
  uniqueVisitors: number;
  pageViews: number;
  bounceRate: number;
  averageSessionDuration: number;
  topContent: Array<{
    contentId: string;
    title: string;
    views: number;
    category: string;
  }>;
  trafficSources: Array<{
    source: string;
    views: number;
    percentage: number;
  }>;
}

export interface CreatorDashboardAnalytics {
  totalViews: number;
  totalRevenue: number;
  totalSubscribers: number;
  totalContent: number;
  averageRating: number;
  topPerformingContent: Array<{
    contentId: string;
    title: string;
    views: number;
    revenue: number;
    rating: number;
  }>;
  recentActivity: Array<{
    type: string;
    description: string;
    timestamp: Date;
    value?: number;
  }>;
}

class AnalyticsService {
  async trackView(contentId: string, userId: string, watchTime: number = 0): Promise<void> {
    try {
      await addDoc(collection(db, 'analytics'), {
        contentId,
        userId,
        type: 'view',
        timestamp: serverTimestamp(),
        data: {
          watchTime,
          sessionId: this.generateSessionId()
        }
      });
    } catch (error) {
      console.error('Error tracking view:', error);
    }
  }

  async trackEngagement(contentId: string, userId: string, engagementType: string, data: any = {}): Promise<void> {
    try {
      await addDoc(collection(db, 'analytics'), {
        contentId,
        userId,
        type: 'engagement',
        timestamp: serverTimestamp(),
        data: {
          engagementType,
          ...data
        }
      });
    } catch (error) {
      console.error('Error tracking engagement:', error);
    }
  }

  async trackRevenue(contentId: string, amount: number, revenueType: 'ad' | 'donation' | 'subscription'): Promise<void> {
    try {
      await addDoc(collection(db, 'analytics'), {
        contentId,
        type: 'revenue',
        timestamp: serverTimestamp(),
        data: {
          amount,
          revenueType
        }
      });
    } catch (error) {
      console.error('Error tracking revenue:', error);
    }
  }

  async trackDemographic(contentId: string, userId: string, age: number, gender: string, location: string): Promise<void> {
    try {
      await addDoc(collection(db, 'analytics'), {
        contentId,
        userId,
        type: 'demographic',
        timestamp: serverTimestamp(),
        data: {
          age,
          gender,
          location
        }
      });
    } catch (error) {
      console.error('Error tracking demographic:', error);
    }
  }

  async getViewAnalytics(contentId: string, startDate?: Date, endDate?: Date): Promise<ViewAnalytics> {
    try {
      let q = query(
        collection(db, 'analytics'),
        where('contentId', '==', contentId),
        where('type', '==', 'view'),
        orderBy('timestamp', 'desc')
      );

      if (startDate) {
        q = query(q, where('timestamp', '>=', Timestamp.fromDate(startDate)));
      }
      if (endDate) {
        q = query(q, where('timestamp', '<=', Timestamp.fromDate(endDate)));
      }

      const snapshot = await getDocs(q);
      const views = snapshot.docs.map(doc => doc.data());
      
      const uniqueUsers = new Set(views.map(v => v.userId));
      const totalWatchTime = views.reduce((sum, v) => sum + (v.data.watchTime || 0), 0);
      
      const viewsOverTime = this.aggregateViewsOverTime(views);
      
      return {
        contentId,
        views: views.length,
        uniqueViews: uniqueUsers.size,
        watchTime: totalWatchTime,
        completionRate: this.calculateCompletionRate(views),
        viewsOverTime
      };
    } catch (error) {
      console.error('Error getting view analytics:', error);
      throw error;
    }
  }

  async getRevenueAnalytics(contentId: string, startDate?: Date, endDate?: Date): Promise<RevenueAnalytics> {
    try {
      let q = query(
        collection(db, 'analytics'),
        where('contentId', '==', contentId),
        where('type', '==', 'revenue'),
        orderBy('timestamp', 'desc')
      );

      if (startDate) {
        q = query(q, where('timestamp', '>=', Timestamp.fromDate(startDate)));
      }
      if (endDate) {
        q = query(q, where('timestamp', '<=', Timestamp.fromDate(endDate)));
      }

      const snapshot = await getDocs(q);
      const revenues = snapshot.docs.map(doc => doc.data());
      
      const totalRevenue = revenues.reduce((sum, r) => sum + r.data.amount, 0);
      const adRevenue = revenues.filter(r => r.data.revenueType === 'ad').reduce((sum, r) => sum + r.data.amount, 0);
      const donationRevenue = revenues.filter(r => r.data.revenueType === 'donation').reduce((sum, r) => sum + r.data.amount, 0);
      const subscriptionRevenue = revenues.filter(r => r.data.revenueType === 'subscription').reduce((sum, r) => sum + r.data.amount, 0);
      
      const revenueOverTime = this.aggregateRevenueOverTime(revenues);
      
      return {
        contentId,
        totalRevenue,
        adRevenue,
        donationRevenue,
        subscriptionRevenue,
        revenueOverTime
      };
    } catch (error) {
      console.error('Error getting revenue analytics:', error);
      throw error;
    }
  }

  async getDemographicAnalytics(contentId: string): Promise<DemographicAnalytics> {
    try {
      const q = query(
        collection(db, 'analytics'),
        where('contentId', '==', contentId),
        where('type', '==', 'demographic')
      );

      const snapshot = await getDocs(q);
      const demographics = snapshot.docs.map(doc => doc.data());
      
      const ageGroups = this.calculateAgeGroups(demographics);
      const genderDistribution = this.calculateGenderDistribution(demographics);
      const locationDistribution = this.calculateLocationDistribution(demographics);
      
      return {
        contentId,
        ageGroups,
        genderDistribution,
        locationDistribution
      };
    } catch (error) {
      console.error('Error getting demographic analytics:', error);
      throw error;
    }
  }

  async getEngagementAnalytics(contentId: string): Promise<EngagementAnalytics> {
    try {
      const q = query(
        collection(db, 'analytics'),
        where('contentId', '==', contentId),
        where('type', '==', 'engagement')
      );

      const snapshot = await getDocs(q);
      const engagements = snapshot.docs.map(doc => doc.data());
      
      const likes = engagements.filter(e => e.data.engagementType === 'like').length;
      const dislikes = engagements.filter(e => e.data.engagementType === 'dislike').length;
      const comments = engagements.filter(e => e.data.engagementType === 'comment').length;
      const shares = engagements.filter(e => e.data.engagementType === 'share').length;
      const bookmarks = engagements.filter(e => e.data.engagementType === 'bookmark').length;
      const subscriptions = engagements.filter(e => e.data.engagementType === 'subscribe').length;
      
      const totalEngagements = likes + dislikes + comments + shares + bookmarks + subscriptions;
      const viewSnapshot = await getDocs(query(collection(db, 'analytics'), where('contentId', '==', contentId), where('type', '==', 'view')));
      const totalViews = viewSnapshot.docs.length;
      
      return {
        contentId,
          likes,
        dislikes,
        comments,
        shares,
        bookmarks,
        subscriptions,
        engagementRate: totalViews > 0 ? (totalEngagements / totalViews) * 100 : 0,
        averageWatchTime: this.calculateAverageWatchTime(engagements),
        retentionRate: this.calculateRetentionRate(engagements)
      };
    } catch (error) {
      console.error('Error getting engagement analytics:', error);
      throw error;
    }
  }

  async getTrafficAnalytics(startDate?: Date, endDate?: Date): Promise<TrafficAnalytics> {
    try {
      let q = query(
        collection(db, 'analytics'),
        where('type', '==', 'view'),
        orderBy('timestamp', 'desc')
      );

      if (startDate) {
        q = query(q, where('timestamp', '>=', Timestamp.fromDate(startDate)));
      }
      if (endDate) {
        q = query(q, where('timestamp', '<=', Timestamp.fromDate(endDate)));
      }

      const snapshot = await getDocs(q);
      const views = snapshot.docs.map(doc => doc.data());
      
      const uniqueUsers = new Set(views.map(v => v.userId));
      const totalViews = views.length;
      const uniqueVisitors = uniqueUsers.size;
      
      const topContent = await this.getTopContent(views);
      const trafficSources = this.calculateTrafficSources(views);
      
      return {
        totalViews,
        uniqueVisitors,
        pageViews: totalViews,
        bounceRate: this.calculateBounceRate(views),
        averageSessionDuration: this.calculateAverageSessionDuration(views),
        topContent,
        trafficSources
      };
    } catch (error) {
      console.error('Error getting traffic analytics:', error);
      throw error;
    }
  }

  async getCreatorDashboardAnalytics(userId: string): Promise<CreatorDashboardAnalytics> {
    try {
      // Add timeout for mobile devices to prevent hanging
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Analytics request timeout')), 30000); // 30 second timeout
      });

      const analyticsPromise = (async () => {
        const contentSnapshot = await getDocs(query(collection(db, 'content'), where('uploaderId', '==', userId)));
        const userContent = contentSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as any));
        
        let totalViews = 0;
        let totalRevenue = 0;
        let totalSubscribers = 0;
        let totalRatings = 0;
        let ratingSum = 0;
        
        const topPerformingContent: Array<{
          contentId: string;
          title: string;
          views: number;
          revenue: number;
          rating: number;
        }> = [];
        
        // Process content in batches to avoid overwhelming mobile devices
        const batchSize = 5;
        for (let i = 0; i < userContent.length; i += batchSize) {
          const batch = userContent.slice(i, i + batchSize);
          await Promise.all(batch.map(async (content) => {
            try {
              const [viewAnalytics, revenueAnalytics] = await Promise.all([
                this.getViewAnalytics(content.id).catch(() => ({ views: 0 })),
                this.getRevenueAnalytics(content.id).catch(() => ({ totalRevenue: 0 }))
              ]);
              
              totalViews += viewAnalytics.views || 0;
              totalRevenue += revenueAnalytics.totalRevenue || 0;
              
              if (content.rating) {
                totalRatings++;
                ratingSum += content.rating;
              }
              
              topPerformingContent.push({
                contentId: content.id,
                title: content.title,
                views: viewAnalytics.views || 0,
                revenue: revenueAnalytics.totalRevenue || 0,
                rating: content.rating || 0
              });
            } catch (error) {
              console.error(`Error processing content ${content.id}:`, error);
              // Continue with other content even if one fails
            }
          }));
        }
        
        const recentActivity = await this.getRecentActivity(userId).catch(() => []);
        
        return {
          totalViews,
          totalRevenue,
          totalSubscribers,
          totalContent: userContent.length,
          averageRating: totalRatings > 0 ? ratingSum / totalRatings : 0,
          topPerformingContent: topPerformingContent.sort((a, b) => b.views - a.views).slice(0, 10),
          recentActivity
        };
      })();

      return await Promise.race([analyticsPromise, timeoutPromise]);
    } catch (error) {
      console.error('Error getting creator dashboard analytics:', error);
      // Return default values instead of throwing to prevent UI from hanging
      return {
        totalViews: 0,
        totalRevenue: 0,
        totalSubscribers: 0,
        totalContent: 0,
        averageRating: 0,
        topPerformingContent: [],
        recentActivity: []
      };
    }
  }

  private generateSessionId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  private aggregateViewsOverTime(views: any[]): Array<{ date: string; views: number; uniqueViews: number }> {
    const dailyViews: Record<string, { views: number; uniqueUsers: Set<string> }> = {};
    
    views.forEach(view => {
      const date = new Date(view.timestamp.seconds * 1000).toISOString().split('T')[0];
      if (!dailyViews[date]) {
        dailyViews[date] = { views: 0, uniqueUsers: new Set() };
      }
      dailyViews[date].views++;
      dailyViews[date].uniqueUsers.add(view.userId);
    });
    
    return Object.entries(dailyViews).map(([date, data]) => ({
      date,
      views: data.views,
      uniqueViews: data.uniqueUsers.size
    })).sort((a, b) => a.date.localeCompare(b.date));
  }

  private aggregateRevenueOverTime(revenues: any[]): Array<{ date: string; revenue: number; adRevenue: number; donationRevenue: number }> {
    const dailyRevenue: Record<string, { total: number; ad: number; donation: number }> = {};
    
    revenues.forEach(revenue => {
      const date = new Date(revenue.timestamp.seconds * 1000).toISOString().split('T')[0];
      if (!dailyRevenue[date]) {
        dailyRevenue[date] = { total: 0, ad: 0, donation: 0 };
      }
      dailyRevenue[date].total += revenue.data.amount;
      if (revenue.data.revenueType === 'ad') {
        dailyRevenue[date].ad += revenue.data.amount;
      } else if (revenue.data.revenueType === 'donation') {
        dailyRevenue[date].donation += revenue.data.amount;
      }
    });
    
    return Object.entries(dailyRevenue).map(([date, data]) => ({
      date,
      revenue: data.total,
      adRevenue: data.ad,
      donationRevenue: data.donation
    })).sort((a, b) => a.date.localeCompare(b.date));
  }

  private calculateAgeGroups(demographics: any[]): any {
    const ageGroups = {
      '13-17': 0,
      '18-24': 0,
      '25-34': 0,
      '35-44': 0,
      '45-54': 0,
      '55-64': 0,
      '65+': 0
    };
    
    demographics.forEach(demo => {
      const age = demo.data.age;
      if (age >= 13 && age <= 17) ageGroups['13-17']++;
      else if (age >= 18 && age <= 24) ageGroups['18-24']++;
      else if (age >= 25 && age <= 34) ageGroups['25-34']++;
      else if (age >= 35 && age <= 44) ageGroups['35-44']++;
      else if (age >= 45 && age <= 54) ageGroups['45-54']++;
      else if (age >= 55 && age <= 64) ageGroups['55-64']++;
      else if (age >= 65) ageGroups['65+']++;
    });
    
    return ageGroups;
  }

  private calculateGenderDistribution(demographics: any[]): any {
    const distribution = {
      male: 0,
      female: 0,
      other: 0,
      preferNotToSay: 0
    };
    
    demographics.forEach(demo => {
      const gender = demo.data.gender?.toLowerCase();
      if (gender === 'male') distribution.male++;
      else if (gender === 'female') distribution.female++;
      else if (gender === 'other') distribution.other++;
      else distribution.preferNotToSay++;
    });
    
    return distribution;
  }

  private calculateLocationDistribution(demographics: any[]): Array<{ country: string; views: number; percentage: number }> {
    const locationCount: Record<string, number> = {};
    
    demographics.forEach(demo => {
      const location = demo.data.location;
      if (location) {
        locationCount[location] = (locationCount[location] || 0) + 1;
      }
    });
    
    const total = demographics.length;
    return Object.entries(locationCount)
      .map(([country, views]) => ({
        country,
        views,
        percentage: total > 0 ? (views / total) * 100 : 0
      }))
      .sort((a, b) => b.views - a.views)
      .slice(0, 10);
  }

  private calculateCompletionRate(views: any[]): number {
    const completedViews = views.filter(v => v.data.watchTime && v.data.watchTime > 0).length;
    return views.length > 0 ? (completedViews / views.length) * 100 : 0;
  }

  private calculateAverageWatchTime(engagements: any[]): number {
    const watchTimes = engagements
      .filter(e => e.data.watchTime)
      .map(e => e.data.watchTime);
    
    return watchTimes.length > 0 
      ? watchTimes.reduce((sum, time) => sum + time, 0) / watchTimes.length 
      : 0;
  }

  private calculateRetentionRate(engagements: any[]): number {
    const totalEngagements = engagements.length;
    const returnEngagements = engagements.filter(e => e.data.isReturning).length;
    return totalEngagements > 0 ? (returnEngagements / totalEngagements) * 100 : 0;
  }

  private calculateBounceRate(views: any[]): number {
    const singlePageViews = views.filter(v => v.data.isBounce).length;
    return views.length > 0 ? (singlePageViews / views.length) * 100 : 0;
  }

  private calculateAverageSessionDuration(views: any[]): number {
    const sessionDurations = views
      .filter(v => v.data.sessionDuration)
      .map(v => v.data.sessionDuration);
    
    return sessionDurations.length > 0 
      ? sessionDurations.reduce((sum, duration) => sum + duration, 0) / sessionDurations.length 
      : 0;
  }

  private async getTopContent(views: any[]): Promise<Array<{ contentId: string; title: string; views: number; category: string }>> {
    const contentViews: Record<string, number> = {};
    
    views.forEach(view => {
      contentViews[view.contentId] = (contentViews[view.contentId] || 0) + 1;
    });
    
    const topContentIds = Object.entries(contentViews)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([contentId]) => contentId);
    
    const topContent = [];
    for (const contentId of topContentIds) {
      try {
        const contentDoc = await getDoc(doc(db, 'content', contentId));
        if (contentDoc.exists()) {
          const contentData = contentDoc.data();
          topContent.push({
            contentId,
            title: contentData.title,
            views: contentViews[contentId],
            category: contentData.category
          });
        }
      } catch (error) {
        console.error('Error fetching content:', error);
      }
    }
    
    return topContent;
  }

  private calculateTrafficSources(views: any[]): Array<{ source: string; views: number; percentage: number }> {
    const sources: Record<string, number> = {};
    
    views.forEach(view => {
      const source = view.data.source || 'direct';
      sources[source] = (sources[source] || 0) + 1;
    });
    
    const total = views.length;
    return Object.entries(sources)
      .map(([source, views]) => ({
        source,
        views,
        percentage: total > 0 ? (views / total) * 100 : 0
      }))
      .sort((a, b) => b.views - a.views);
  }

  private async getRecentActivity(userId: string): Promise<Array<{ type: string; description: string; timestamp: Date; value?: number }>> {
    try {
      const q = query(
        collection(db, 'analytics'),
        where('userId', '==', userId),
        orderBy('timestamp', 'desc'),
        limit(10)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          type: data.type,
          description: this.getActivityDescription(data),
          timestamp: new Date(data.timestamp.seconds * 1000),
          value: data.data.amount || data.data.watchTime
        };
      });
    } catch (error) {
      console.error('Error getting recent activity:', error);
      return [];
    }
  }

  private getActivityDescription(data: any): string {
    switch (data.type) {
      case 'view':
        return `Viewed content`;
      case 'engagement':
        return `${data.data.engagementType} interaction`;
      case 'revenue':
        return `Earned $${data.data.amount} from ${data.data.revenueType}`;
      default:
        return 'Activity recorded';
    }
  }
}

const analyticsService = new AnalyticsService();
export default analyticsService;
export { AnalyticsService };